import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { groupsApi } from "@/services/api";
import { useDebounce } from "@/hooks/use-debounce";
import type { Group } from "@/types/syra";
import { supabase } from "@/integrations/supabase/client";
import { useOrgRole } from "@/hooks/useOrgRole";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { Search, Wifi, WifiOff, QrCode, Settings2, Plus, RefreshCw, Check, Archive, ArchiveRestore, Pencil, Volume2, VolumeX, Bell, Brain } from "lucide-react";
import { motion } from "framer-motion";
import { RoleGate } from "@/components/RoleGate";
import { JarvisWhatsAppToggle } from "@/components/jarvis/JarvisWhatsAppToggle";
import { GroupAdminsSelector } from "@/components/groups/GroupAdminsSelector";
import { GroupUsagePanel, useGroupsUsage, type UsagePeriod } from "@/components/groups/GroupUsagePanel";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

// ── Group Mode Badge (fetches from group_profiles) ──────────────────
function GroupModeBadge({ groupId }: { groupId: string }) {
  const { data } = useQuery({
    queryKey: ["group-profile", groupId],
    queryFn: async () => {
      const { data } = await supabase.from("group_profiles").select("mode").eq("group_id", groupId).maybeSingle();
      return data;
    },
    staleTime: 60000,
  });
  const mode = (data?.mode as string) || "mention";
  const modeLabels: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
    silent: { label: "🔇 Silencioso", variant: "secondary" },
    mention: { label: "🔔 Menção", variant: "outline" },
    active: { label: "🔊 Ativo", variant: "default" },
  };
  const { label, variant } = modeLabels[mode] || modeLabels.mention;
  return <Badge variant={variant} className="text-xs">{label}</Badge>;
}

// ── Group Mode Selector + Mention Aliases ──────────────────────────
const PRESET_ALIASES = ["Syra", "Jarvis", "Atena"];

function GroupModeSelector({ groupId }: { groupId: string }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data: profile } = useQuery({
    queryKey: ["group-profile", groupId],
    queryFn: async () => {
      const { data } = await supabase.from("group_profiles").select("*").eq("group_id", groupId).maybeSingle();
      return data;
    },
  });
  const mode = (profile?.mode as string) || "mention";
  const dbAliases = Array.isArray((profile as any)?.mention_aliases)
    ? ((profile as any).mention_aliases as string[]).filter((s) => typeof s === "string" && s.trim().length > 0)
    : ["syra"];
  const [aliasInput, setAliasInput] = useState("");
  const [aliases, setAliases] = useState<string[]>(dbAliases);

  // Sync local state when profile (re)loads
  useEffect(() => {
    if (profile) setAliases(dbAliases.length ? dbAliases : ["syra"]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  const modeMut = useMutation({
    mutationFn: async (newMode: string) => {
      if (profile) {
        await supabase.from("group_profiles").update({ mode: newMode } as any).eq("group_id", groupId);
      } else {
        await supabase.from("group_profiles").insert({ group_id: groupId, mode: newMode, mention_aliases: ["syra"] } as any);
      }
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["group-profile", groupId] }); toast({ title: "Modo atualizado" }); },
  });

  const aliasesMut = useMutation({
    mutationFn: async (next: string[]) => {
      const clean = Array.from(new Set(next.map((s) => s.trim().toLowerCase()).filter(Boolean)));
      const final = clean.length ? clean : ["syra"];
      if (profile) {
        await supabase.from("group_profiles").update({ mention_aliases: final } as any).eq("group_id", groupId);
      } else {
        await supabase.from("group_profiles").insert({ group_id: groupId, mode: "mention", mention_aliases: final } as any);
      }
      return final;
    },
    onSuccess: (final) => {
      setAliases(final);
      qc.invalidateQueries({ queryKey: ["group-profile", groupId] });
      toast({ title: "Formas de menção salvas", description: final.map((a) => `@${a}`).join("  ·  ") });
    },
  });

  const addAlias = (raw: string) => {
    const v = raw.trim().toLowerCase();
    if (!v) return;
    if (aliases.includes(v)) { setAliasInput(""); return; }
    aliasesMut.mutate([...aliases, v]);
    setAliasInput("");
  };
  const removeAlias = (a: string) => aliasesMut.mutate(aliases.filter((x) => x !== a));

  return (
    <div className="space-y-3">
      <Select value={mode} onValueChange={(v) => modeMut.mutate(v)}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="silent">🔇 Silencioso</SelectItem>
          <SelectItem value="mention">🔔 Menção (apenas quando chamada)</SelectItem>
          <SelectItem value="active">🔊 Ativo (responde a tudo)</SelectItem>
        </SelectContent>
      </Select>

      {mode === "mention" && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-xs font-semibold text-foreground">Como me chamam neste grupo?</p>
              <p className="text-[11px] text-muted-foreground leading-snug">
                A Syra só responde quando algum desses nomes aparece na mensagem (ou um @ direto).
                Use o nome do dia-a-dia do grupo.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5">
            {aliases.map((a) => (
              <Badge key={a} variant="secondary" className="gap-1.5 pl-2 pr-1 py-0.5">
                <span className="text-xs">@{a}</span>
                <button
                  type="button"
                  onClick={() => removeAlias(a)}
                  className="hover:text-destructive text-muted-foreground text-xs leading-none"
                  aria-label={`Remover ${a}`}
                  disabled={aliases.length <= 1}
                  title={aliases.length <= 1 ? "Mantenha pelo menos um nome" : "Remover"}
                >
                  ×
                </button>
              </Badge>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Input
              value={aliasInput}
              onChange={(e) => setAliasInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAlias(aliasInput); } }}
              placeholder="ex: atena, jeff, syrinha"
              className="h-8 text-xs"
              maxLength={32}
            />
            <Button
              size="sm"
              variant="secondary"
              type="button"
              onClick={() => addAlias(aliasInput)}
              disabled={!aliasInput.trim() || aliasesMut.isPending}
              className="h-8"
            >
              <Plus className="w-3.5 h-3.5" />
            </Button>
          </div>

          <div className="flex flex-wrap gap-1 pt-1">
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground self-center mr-1">Sugestões:</span>
            {PRESET_ALIASES.filter((p) => !aliases.includes(p.toLowerCase())).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => addAlias(p)}
                className="text-[11px] px-2 py-0.5 rounded-full border border-border hover:bg-accent hover:text-accent-foreground transition-colors"
              >
                + {p}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const statusConfig = {
  connected: { icon: <Wifi className="w-4 h-4" />, color: "text-success", bg: "bg-success/15", label: "Conectado" },
  disconnected: { icon: <WifiOff className="w-4 h-4" />, color: "text-destructive", bg: "bg-destructive/15", label: "Desconectado" },
  qr_pending: { icon: <QrCode className="w-4 h-4" />, color: "text-warning", bg: "bg-warning/15", label: "QR Pendente" },
};

export default function Groups() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editGroup, setEditGroup] = useState<Group | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [editingNameId, setEditingNameId] = useState<string | null>(null);
  const [editingNameValue, setEditingNameValue] = useState("");
  const [usagePeriod, setUsagePeriod] = useState<UsagePeriod>("day");
  const debouncedSearch = useDebounce(search);

  const { data: groups = [], isLoading } = useQuery({ queryKey: ["groups"], queryFn: groupsApi.list });
  const { data: usageMap, isLoading: usageLoading } = useGroupsUsage(usagePeriod);

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => groupsApi.toggleActive(id, active),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["groups"] }); toast({ title: "Grupo atualizado" }); },
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Group> }) => groupsApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["groups"] }); setEditGroup(null); toast({ title: "Configuração salva" }); },
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => groupsApi.update(id, { name }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["groups"] }); setEditingNameId(null); toast({ title: "Nome atualizado" }); },
  });

  const createMut = useMutation({
    mutationFn: (group: { name: string; group_jid: string; org_id: string; config?: Record<string, unknown> }) => groupsApi.create(group),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["groups"] }); setShowCreate(false); toast({ title: "Grupo criado com sucesso" }); },
    onError: (err: Error) => { toast({ title: "Erro ao criar grupo", description: err.message, variant: "destructive" }); },
  });

  const activeGroups = groups.filter((g) => g.active);
  const archivedGroups = groups.filter((g) => !g.active);
  const displayGroups = showArchived ? archivedGroups : activeGroups;
  const filtered = displayGroups.filter((g) => g.name.toLowerCase().includes(debouncedSearch.toLowerCase()));

  const startRename = (g: { id: string; name: string }) => { setEditingNameId(g.id); setEditingNameValue(g.name); };
  const submitRename = (id: string) => { const trimmed = editingNameValue.trim(); if (!trimmed) return; renameMut.mutate({ id, name: trimmed }); };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground font-headline tracking-tight">Gestão de Grupos</h1>
          <p className="text-muted-foreground mt-1">Gerencie seus grupos de WhatsApp</p>
        </div>
        <RoleGate permission="groups.create">
          <Button onClick={() => { setEditGroup(null); setShowCreate(true); }} className="shadow-sm">
            <Plus className="w-4 h-4 mr-1" /> Novo Grupo
          </Button>
        </RoleGate>
      </div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="flex items-center gap-4"
      >
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Buscar grupos..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Button
          variant={showArchived ? "default" : "outline"}
          size="sm"
          onClick={() => setShowArchived(!showArchived)}
        >
          {showArchived ? <ArchiveRestore className="w-4 h-4 mr-1" /> : <Archive className="w-4 h-4 mr-1" />}
          {showArchived ? "Ver Ativos" : `Arquivados (${archivedGroups.length})`}
        </Button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Consumo:</span>
          <ToggleGroup
            type="single"
            size="sm"
            value={usagePeriod}
            onValueChange={(v) => v && setUsagePeriod(v as UsagePeriod)}
            className="border border-border rounded-md"
          >
            <ToggleGroupItem value="day" className="text-xs px-3 h-8">Dia</ToggleGroupItem>
            <ToggleGroupItem value="week" className="text-xs px-3 h-8">Semana</ToggleGroupItem>
            <ToggleGroupItem value="month" className="text-xs px-3 h-8">Mês</ToggleGroupItem>
          </ToggleGroup>
        </div>
      </motion.div>

      {/* Cards Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-52 w-full rounded-lg" />)}
        </div>
      ) : filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <Archive className="w-12 h-12 mb-3 opacity-30" />
          <p className="text-sm">{showArchived ? "Nenhum grupo arquivado." : "Nenhum grupo encontrado."}</p>
        </motion.div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {filtered.map((g, index) => {
            const config = (typeof g.config === 'object' && g.config !== null ? g.config : {}) as Record<string, unknown>;
            const isEditingName = editingNameId === g.id;
            const status = statusConfig[g.instance_status];
            return (
              <motion.div
                key={g.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45, delay: index * 0.06, ease: [0.22, 1, 0.36, 1] }}
              >
                <Card className={`relative overflow-hidden border-border/50 shadow-sm hover:shadow-lg hover:scale-[1.02] hover:-translate-y-0.5 transition-all duration-300 ease-out ${!g.active ? "opacity-60" : ""}`}>
                  {/* Status indicator strip */}
                  <div className={`absolute top-0 left-0 right-0 h-0.5 ${g.active ? "bg-success" : "bg-muted-foreground/30"}`} />
                  
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      <div className={`w-8 h-8 rounded-lg ${status.bg} ${status.color} flex items-center justify-center shrink-0`}>
                        {status.icon}
                      </div>
                      {isEditingName ? (
                        <form className="flex items-center gap-1 flex-1" onSubmit={(e) => { e.preventDefault(); submitRename(g.id); }}>
                          <Input value={editingNameValue} onChange={(e) => setEditingNameValue(e.target.value)} className="h-7 text-sm" autoFocus onBlur={() => setEditingNameId(null)} onKeyDown={(e) => { if (e.key === "Escape") setEditingNameId(null); }} />
                        </form>
                      ) : (
                        <div className="flex items-center gap-1 min-w-0">
                          <CardTitle className="text-base truncate font-semibold">{g.name}</CardTitle>
                          <button onClick={() => startRename(g)} className="text-muted-foreground hover:text-foreground shrink-0 transition-colors">
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </div>
                    {!showArchived && (
                      <Switch checked={g.active} onCheckedChange={(checked) => toggleMut.mutate({ id: g.id, active: checked })} />
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="font-medium text-foreground">{g.member_count}</span> membros
                      <span className="text-border">•</span>
                      <Badge variant={g.active ? "default" : "secondary"} className="text-xs">
                        {g.active ? "Ativo" : "Arquivado"}
                      </Badge>
                      <GroupModeBadge groupId={g.id} />
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {config.welcome_enabled && <Badge variant="outline" className="text-xs bg-primary/5 border-primary/20">Welcome</Badge>}
                      {config.commands_enabled && <Badge variant="outline" className="text-xs bg-info/5 border-info/20">Comandos</Badge>}
                    </div>
                    <GroupUsagePanel groupId={g.id} usage={usageMap?.get(g.id)} loading={usageLoading} />
                    {g.active && <JarvisWhatsAppToggle groupId={g.id} />}
                    <div className="flex gap-2 pt-1">
                      <Button variant="ghost" size="sm" asChild className="hover:bg-primary/5 hover:text-primary">
                        <Link to={`/groups/${g.id}/mental-map`}>
                          <Brain className="w-4 h-4 mr-1" /> Mapa
                        </Link>
                      </Button>
                      <Button variant="ghost" size="sm" className="flex-1 hover:bg-primary/5 hover:text-primary" onClick={() => { setShowCreate(false); setEditGroup({ ...g, config: config as unknown as Group["config"] }); }}>
                        <Settings2 className="w-4 h-4 mr-1" /> Configurar
                      </Button>
                      <RoleGate permission="groups.update">
                        {g.active ? (
                          <Button variant="ghost" size="sm" onClick={() => toggleMut.mutate({ id: g.id, active: false })} title="Arquivar grupo" className="hover:bg-warning/10 hover:text-warning">
                            <Archive className="w-4 h-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" onClick={() => toggleMut.mutate({ id: g.id, active: true })} title="Restaurar grupo" className="hover:bg-success/10 hover:text-success">
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        )}
                      </RoleGate>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Create Group Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Novo Grupo</DialogTitle></DialogHeader>
          <CreateGroupForm onSave={(data) => createMut.mutate(data)} loading={createMut.isPending} />
        </DialogContent>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog open={!!editGroup} onOpenChange={() => setEditGroup(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Configurar: {editGroup?.name}</DialogTitle></DialogHeader>
          {editGroup && <GroupConfigForm group={editGroup} onSave={(data) => updateMut.mutate({ id: editGroup.id, data })} loading={updateMut.isPending} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

type WhatsAppGroup = { id: string; subject: string; size: number };

function CreateGroupForm({ onSave, loading }: { onSave: (d: { name: string; group_jid: string; org_id: string; config: Record<string, unknown> }) => void; loading: boolean }) {
  const { toast } = useToast();
  const { orgId } = useOrgRole();
  const [name, setName] = useState("");
  const [groupJid, setGroupJid] = useState("");
  const [instanceName, setInstanceName] = useState("");
  const [waGroups, setWaGroups] = useState<WhatsAppGroup[]>([]);
  const [fetching, setFetching] = useState(false);
  const [showList, setShowList] = useState(false);

  const fetchGroups = async () => {
    if (!instanceName.trim()) { toast({ title: "Informe o nome da instância", variant: "destructive" }); return; }
    setFetching(true);
    try {
      const session = (await supabase.auth.getSession()).data.session;
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/list-whatsapp-groups?instance=${encodeURIComponent(instanceName.trim())}&org_id=${encodeURIComponent(orgId ?? "")}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${session?.access_token}`, apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY } });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Erro ao buscar grupos");
      setWaGroups(result.groups || []);
      setShowList(true);
      if ((result.groups || []).length === 0) toast({ title: "Nenhum grupo encontrado nessa instância" });
    } catch (err: any) {
      toast({ title: "Erro ao buscar grupos", description: err.message, variant: "destructive" });
    } finally { setFetching(false); }
  };

  const selectGroup = (g: WhatsAppGroup) => { setName(g.subject); setGroupJid(g.id); setShowList(false); };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !orgId) return;
    onSave({ name: name.trim(), group_jid: groupJid.trim(), org_id: orgId, config: instanceName.trim() ? { instance_name: instanceName.trim() } : {} });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Importar do WhatsApp</label>
        <div className="flex gap-2">
          <Input value={instanceName} onChange={(e) => setInstanceName(e.target.value)} placeholder="Nome da instância (Evolution API)" className="flex-1" />
          <Button type="button" variant="outline" onClick={fetchGroups} disabled={fetching}>
            <RefreshCw className={`w-4 h-4 mr-1 ${fetching ? "animate-spin" : ""}`} /> Buscar
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">Informe o nome da instância para listar os grupos automaticamente.</p>
      </div>
      {showList && waGroups.length > 0 && (
        <div className="max-h-48 overflow-y-auto border rounded-md divide-y divide-border">
          {waGroups.map((g) => (
            <button key={g.id} type="button" onClick={() => selectGroup(g)}
              className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent transition-colors ${groupJid === g.id ? "bg-accent" : ""}`}>
              <span className="truncate text-foreground">{g.subject}</span>
              <span className="flex items-center gap-2 text-muted-foreground text-xs shrink-0">
                {g.size} membros
                {groupJid === g.id && <Check className="w-4 h-4 text-primary" />}
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Nome do Grupo</label>
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Ex: Comunidade Tech" required />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">WhatsApp JID (Group JID)</label>
        <Input value={groupJid} onChange={(e) => setGroupJid(e.target.value)} placeholder="Ex: 5511999999999-1234567890@g.us" />
        <p className="text-xs text-muted-foreground">Preenchido automaticamente ao selecionar um grupo acima.</p>
      </div>
      <Button type="submit" disabled={loading || !name.trim()} className="w-full">{loading ? "Criando..." : "Criar Grupo"}</Button>
    </form>
  );
}

function GroupConfigForm({ group, onSave, loading }: { group: Group; onSave: (d: Partial<Group>) => void; loading: boolean }) {
  const qc = useQueryClient();
  const { toast: toastFn } = useToast();
  const { data: liveGroup } = useQuery({
    queryKey: ["group-live", group.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_groups")
        .select("id, sandbox_mode")
        .eq("id", group.id)
        .single();
      if (error) throw error;
      return data;
    },
    initialData: { id: group.id, sandbox_mode: Boolean(group.sandbox_mode) },
    refetchOnWindowFocus: false,
    refetchInterval: 3000,
  });

  useEffect(() => {
    const channel = supabase
      .channel(`group-config-${group.id}`)
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "syra_groups", filter: `id=eq.${group.id}` },
        (payload) => {
          const nextSandbox = Boolean((payload.new as { sandbox_mode?: boolean } | null)?.sandbox_mode);
          qc.setQueryData(["group-live", group.id], { id: group.id, sandbox_mode: nextSandbox });
          qc.setQueryData(["groups"], (prev: Group[] | undefined) =>
            Array.isArray(prev)
              ? prev.map((item) => (item.id === group.id ? { ...item, sandbox_mode: nextSandbox } : item))
              : prev,
          );
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [group.id, qc]);

  const [config, setConfig] = useState(() => {
    const c = group.config;
    return { ...c, instance_name: c.instance_name || "", summary_periods: c.summary_periods || [], summary_time: c.summary_time || (c as any).summary_schedule || "08:00" };
  });

  // Prompt template state (C-03)
  const { data: groupProfile, isLoading: loadingProfile } = useQuery({
    queryKey: ["group-profile-full", group.id],
    queryFn: async () => {
      const { data } = await supabase.from("group_profiles").select("prompt_template, typology").eq("group_id", group.id).maybeSingle();
      return data;
    },
  });
  const [promptTemplate, setPromptTemplate] = useState("");
  const [typology, setTypology] = useState("");
  const [newFact, setNewFact] = useState({ fact_key: "", fact_value: "", is_required: false, priority: 50 });

  const { data: groupFacts = [] } = useQuery({
    queryKey: ["group-facts", group.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_group_facts")
        .select("id, fact_key, fact_value, is_required, priority, active")
        .eq("group_id", group.id)
        .order("is_required", { ascending: false })
        .order("priority", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: latestMood } = useQuery({
    queryKey: ["group-mood", group.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("syra_group_mood")
        .select("mood_label, confidence, valence, arousal, window_end")
        .eq("group_id", group.id)
        .order("window_end", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });

  // Sync prompt template from DB
  useEffect(() => {
    if (groupProfile) {
      setPromptTemplate(groupProfile.prompt_template || "");
      setTypology(groupProfile.typology || "");
    }
  }, [groupProfile]);

  const savePromptMut = useMutation({
    mutationFn: async () => {
      const updates: Record<string, unknown> = { prompt_template: promptTemplate || null, typology: typology || null };
      const { data: existing } = await supabase.from("group_profiles").select("id").eq("group_id", group.id).maybeSingle();
      if (existing) {
        await supabase.from("group_profiles").update(updates as any).eq("group_id", group.id);
      } else {
        await supabase.from("group_profiles").insert({ group_id: group.id, ...updates } as any);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-profile-full", group.id] });
      toastFn({ title: "Persona salva" });
    },
  });

  const addFactMut = useMutation({
    mutationFn: async () => {
      const payload = {
        org_id: (group as any).org_id,
        group_id: group.id,
        fact_key: newFact.fact_key.trim(),
        fact_value: newFact.fact_value.trim(),
        is_required: newFact.is_required,
        priority: newFact.priority,
        source: "admin_ui",
      };
      const { error } = await supabase.from("syra_group_facts").insert(payload as any);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["group-facts", group.id] });
      setNewFact({ fact_key: "", fact_value: "", is_required: false, priority: 50 });
      toastFn({ title: "Fato do grupo salvo" });
    },
  });

  const toggleFactMut = useMutation({
    mutationFn: async ({ id, active }: { id: string; active: boolean }) => {
      const { error } = await supabase.from("syra_group_facts").update({ active } as any).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["group-facts", group.id] }),
  });

  const togglePeriod = (period: string) => {
    const current = config.summary_periods || [];
    const updated = current.includes(period) ? current.filter((p: string) => p !== period) : [...current, period];
    setConfig({ ...config, summary_periods: updated });
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ config: config as unknown as Group["config"] }); }} className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Instância Evolution API</label>
        <Input value={config.instance_name} onChange={(e) => setConfig({ ...config, instance_name: e.target.value })} placeholder="Nome da instância" />
      </div>
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">Modo do Agente</label>
        <GroupModeSelector groupId={group.id} />
      </div>

      {/* Modo Sandbox */}
      <div className="space-y-2 p-3 rounded-lg border border-warning/30 bg-warning/5">
        <label className="flex items-center justify-between gap-3 text-sm font-medium text-foreground">
          <span className="flex flex-col">
            🧪 Modo Sandbox
            <span className="text-xs font-normal text-muted-foreground">
              Bloqueia envios no WhatsApp e registra tudo em <a href="/sandbox" className="underline">/sandbox</a> para auditoria.
            </span>
          </span>
          <Switch
            checked={Boolean(liveGroup?.sandbox_mode)}
            onCheckedChange={async (v) => {
              await supabase.from("syra_groups").update({ sandbox_mode: v } as any).eq("id", group.id);
              qc.setQueryData(["group-live", group.id], { id: group.id, sandbox_mode: v });
              qc.setQueryData(["groups"], (prev: Group[] | undefined) =>
                Array.isArray(prev)
                  ? prev.map((item) => (item.id === group.id ? { ...item, sandbox_mode: v } : item))
                  : prev,
              );
              qc.invalidateQueries({ queryKey: ["groups"] });
              toastFn({ title: v ? "Sandbox ligado" : "Sandbox desligado" });
            }}
          />
        </label>
      </div>

      {/* Group admins multi-select */}
      <GroupAdminsSelector groupId={group.id} />

      {/* C-03: Persona / Prompt Template */}
      <div className="space-y-3 p-3 bg-muted/50 rounded-lg border border-border/50">
        <label className="text-sm font-medium text-foreground flex items-center gap-2">
          🎭 Persona do Grupo
        </label>
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Tipologia</label>
          <Select value={typology || "none"} onValueChange={(v) => setTypology(v === "none" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Automático</SelectItem>
              <SelectItem value="suporte">Suporte</SelectItem>
              <SelectItem value="vendas">Vendas</SelectItem>
              <SelectItem value="comunidade">Comunidade</SelectItem>
              <SelectItem value="projeto">Projeto</SelectItem>
              <SelectItem value="interno">Interno</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground">Prompt Template (opcional)</label>
          <Textarea
            value={promptTemplate}
            onChange={(e) => setPromptTemplate(e.target.value)}
            placeholder="Ex: Você é um assistente especializado em vendas B2B. Sempre mencione nossos diferenciais..."
            rows={4}
            className="text-sm"
          />
          <p className="text-xs text-muted-foreground">Este template será usado como base para todas as respostas neste grupo, antes do prompt do agente.</p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => savePromptMut.mutate()} disabled={savePromptMut.isPending}>
          {savePromptMut.isPending ? "Salvando..." : "Salvar Persona"}
        </Button>
      </div>

      <div className="space-y-3 p-3 bg-muted/50 rounded-lg border border-border/50">
        <label className="text-sm font-medium text-foreground">Fatos do grupo</label>
        {latestMood && (
          <div className="text-xs text-muted-foreground rounded-md border p-2">
            Humor atual: <span className="text-foreground font-medium">{latestMood.mood_label}</span> · conf. {Number(latestMood.confidence ?? 0).toFixed(2)} · valência {Number(latestMood.valence ?? 0).toFixed(2)} · ativação {Number(latestMood.arousal ?? 0).toFixed(2)}
          </div>
        )}
        <div className="grid gap-2 sm:grid-cols-2">
          <Input placeholder="Chave do fato" value={newFact.fact_key} onChange={(e) => setNewFact({ ...newFact, fact_key: e.target.value })} />
          <Input placeholder="Valor do fato" value={newFact.fact_value} onChange={(e) => setNewFact({ ...newFact, fact_value: e.target.value })} />
          <Input type="number" min="0" max="100" placeholder="Prioridade" value={newFact.priority} onChange={(e) => setNewFact({ ...newFact, priority: Number(e.target.value) })} />
          <label className="flex items-center gap-2 rounded-md border px-3 py-2 text-sm">
            <Checkbox checked={newFact.is_required} onCheckedChange={(v) => setNewFact({ ...newFact, is_required: !!v })} />
            Obrigatório no prompt
          </label>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={() => addFactMut.mutate()} disabled={addFactMut.isPending || !newFact.fact_key.trim() || !newFact.fact_value.trim()}>
          {addFactMut.isPending ? "Salvando..." : "Adicionar fato"}
        </Button>
        <div className="space-y-2">
          {groupFacts.length === 0 ? (
            <p className="text-xs text-muted-foreground">Nenhum fato cadastrado.</p>
          ) : groupFacts.map((fact: any) => (
            <div key={fact.id} className="flex items-start justify-between gap-3 rounded-md border p-2 text-sm">
              <div>
                <div className="font-medium text-foreground">{fact.fact_key} {fact.is_required && <span className="text-xs text-primary">• obrigatório</span>}</div>
                <div className="text-muted-foreground">{fact.fact_value}</div>
              </div>
              <Button type="button" variant="ghost" size="sm" onClick={() => toggleFactMut.mutate({ id: fact.id, active: !fact.active })}>
                {fact.active ? "Desativar" : "Ativar"}
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <label className="text-sm font-medium text-foreground">Funcionalidades</label>
        <div className="space-y-2">
          {[
            { key: "welcome_enabled", label: "Mensagem de boas-vindas" },
            { key: "commands_enabled", label: "Comandos habilitados" },
            { key: "auto_summary", label: "Resumo automático" },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center gap-2 text-sm">
              <Checkbox checked={!!(config as any)[key]} onCheckedChange={(v) => setConfig({ ...config, [key]: !!v })} />
              {label}
            </label>
          ))}
        </div>
      </div>
      {(config as any).auto_summary && (
        <div className="space-y-3 p-3 bg-muted/50 rounded-lg">
          <label className="text-sm font-medium text-foreground">Horário do Resumo</label>
          <Input type="time" value={config.summary_time} onChange={(e) => setConfig({ ...config, summary_time: e.target.value })} />
          <label className="text-sm font-medium text-foreground">Períodos</label>
          <div className="flex flex-wrap gap-2">
            {["6h", "12h", "24h", "48h"].map((p) => (
              <Button key={p} type="button" variant={(config.summary_periods || []).includes(p) ? "default" : "outline"} size="sm" onClick={() => togglePeriod(p)}>
                {p}
              </Button>
            ))}
          </div>
        </div>
      )}
      {(config as any).welcome_enabled && (
        <div className="space-y-2">
          <label className="text-sm font-medium text-foreground">Mensagem de Boas-vindas</label>
          <Textarea value={(config as any).welcome_message || ""} onChange={(e) => setConfig({ ...config, welcome_message: e.target.value } as any)} placeholder="Olá! Bem-vindo ao grupo..." rows={3} />
        </div>
      )}
      <Button type="submit" disabled={loading} className="w-full">{loading ? "Salvando..." : "Salvar Configuração"}</Button>
    </form>
  );
}
